import { showToast } from '../../utils/toast.js';
import { populateVoiceSelect } from '../../services/voiceService.js';
import { fetchVoicePreview, randomPreviewIndex } from '../../services/voicePreview.js';
import { $, $$, on, val, hide } from '../../../core/utils/dom.js';
import { getCurrentLanguage } from '../../translations.js';

export function initializeVoicePreview(voiceInputId, containerId = 'voice-sample-container') {
    const elements = {
        select: document.getElementById(voiceInputId),
        container: document.getElementById(containerId),
        label: document.getElementById('voice-sample-label'),
        playBtn: document.getElementById('play-sample-btn'),
        audio: document.getElementById('voice-sample-player')
    };

    if (!elements.select || !elements.container || !elements.playBtn || !elements.audio) {
        console.error('Voice preview elements missing');
        return;
    }

    let currentElement = elements.select;
    const previewIndexByVoiceId = Object.create(null);

    const genderSelect = document.getElementById('ai-character-gender');

    const getSubDir = () => (genderSelect?.value === 'male' ? 'male_voices' : 'voices');

    const resetUI = () => {
        elements.container.style.display = 'none';
        elements.label && (elements.label.textContent = '');
        elements.playBtn.classList.remove('playing');
        elements.playBtn.innerHTML = '<i class="fas fa-play"></i> <span data-translate="play_sample">Play Sample</span>';
        elements.audio.pause();
        elements.audio.src = '';
        delete elements.playBtn.dataset.liveVoiceId;
    };

    const resetButtonState = () => {
        elements.playBtn.classList.remove('playing');
        elements.playBtn.innerHTML = '<i class="fas fa-play"></i> <span data-translate="play_sample">Play Sample</span>';
    };

    const handleVoiceChange = e => {
        resetUI();
        const voiceId = e.detail?.value ?? e.target?.value ?? currentElement?.getValue?.();
        if (!voiceId) return;

        elements.container.style.display = 'flex';
        elements.playBtn.dataset.liveVoiceId = voiceId;
    };

    const buildDropdown = async () => {
        const subDir = getSubDir();
        const preferredVoiceId =
            currentElement?.getValue?.() ??
            currentElement?.dataset?.value ??
            currentElement?.value ??
            '';

        currentElement.removeEventListener?.('change', handleVoiceChange);
        const parent = currentElement.parentNode;
        parent.removeChild(currentElement);

        const newSelect = document.createElement('select');
        newSelect.id = voiceInputId;

        parent.insertBefore(newSelect, elements.container);
        elements.select = newSelect;

        try {
            const defaultVoiceText = window.translations?.[getCurrentLanguage()]?.['recruit-character']?.select_voice ?? 'Select a Voice';
            const dropdown = await populateVoiceSelect(newSelect, defaultVoiceText, null, subDir);
            currentElement = dropdown || newSelect;
            currentElement.addEventListener('change', handleVoiceChange);

            if (preferredVoiceId && typeof currentElement?.setValue === 'function') {
                currentElement.setValue(preferredVoiceId);
            }
        } catch (err) {
            console.error('Failed to rebuild voice dropdown:', err);
            showToast('Failed to load voices', 'error');
            currentElement = newSelect;
            currentElement.addEventListener('change', handleVoiceChange);
        }
    };

    genderSelect?.addEventListener('change', () => {
        buildDropdown();
    });

    buildDropdown();

    const playLive = async (voiceId) => {
        if (!Object.hasOwn(previewIndexByVoiceId, voiceId)) {
            previewIndexByVoiceId[voiceId] = randomPreviewIndex();
        }
        const previewIndex = previewIndexByVoiceId[voiceId];
        elements.playBtn.disabled = true;
        try {
            const preview = await fetchVoicePreview({
                voiceId,
                previewIndex,
                language: getCurrentLanguage(),
            });
            previewIndexByVoiceId[voiceId] = preview.nextIndex;
            elements.audio.src = preview.objectUrl;
            await elements.audio.play();
        } catch (error) {
            console.error('Live voice preview failed:', error);
            showToast(error.message || 'Voice preview failed', 'error');
        } finally {
            elements.playBtn.disabled = false;
        }
    };

    elements.playBtn.addEventListener('click', async e => {
        e.preventDefault();
        e.stopPropagation();

        const isPlaying = elements.playBtn.classList.contains('playing');
        if (isPlaying) {
            elements.audio.pause();
            return;
        }

        const liveVoiceId = elements.playBtn.dataset.liveVoiceId;
        if (!liveVoiceId) return;
        await playLive(liveVoiceId);
    });

    elements.audio.addEventListener('ended', resetButtonState);
    elements.audio.addEventListener('pause', resetButtonState);
    elements.audio.addEventListener('play', () => {
        elements.playBtn.classList.add('playing');
        elements.playBtn.innerHTML = '<i class="fas fa-pause"></i> <span data-translate="play_sample">Pause Sample</span>';
    });

}

export function initializePreviewModal(modalId, applyBtnId, cancelBtnId, inputId, closeBtnClass = 'close-preview') {
    const modal = $(`#${modalId}`);
    if (!modal) return;
    const hideModal = () => hide(modal);

    on($(`#${applyBtnId}`), 'click', hideModal);
    on($(`#${cancelBtnId}`), 'click', () => {
        const input = $(`#${inputId}`);
        if (input) val(input, '');
        hideModal();
    });
    $$(`.${closeBtnClass}`).forEach(btn => on(btn, 'click', hideModal));
}
